import React, { useEffect } from 'react';
import './App.css';
import './Privacy.css';

const CONTACT_EMAIL = 'walkitoffhealth@gmail.com';
const LAST_UPDATED = 'October 30, 2026';

function Privacy() {
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    document.title = 'Privacy Policy | Walk It Off';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="App">
      <nav className="nav">
        <a href="/" className="logo-container" aria-label="Walk It Off home">
          <img
            src="/walkitofflogo-dark.svg"
            alt="Walk It Off"
            className="logo-horizontal"
            width="350"
            height="130"
          />
        </a>
        <a href="https://apps.apple.com/us/app/walk-it-off-daily-walk-habit/id6818257483" className="nav-cta privacy-nav-cta" target="_blank" rel="noopener noreferrer">
          Download
        </a>
      </nav>

      <main className="privacy-page">
        <div className="privacy-container">
          <h1 className="privacy-title">Privacy Policy</h1>
          <p className="privacy-updated">Last updated: {LAST_UPDATED}</p>

          <p>
            Walk It Off is operated by Abimbola Labs LLC ("Walk It Off", "we", "us", "our"). We respect
            your privacy. This policy explains what
            information we collect through the Walk It Off mobile app (iOS and Android), our website
            walkitoff.xyz, and our waitlist, how we use it, who we share it with, and the choices
            you have.
          </p>

          <h2>Information we collect</h2>
          <ul>
            <li>
              <strong>Google sign-in.</strong> If you sign in with Google, we receive your name,
              email address, and profile picture from your Google account. We do not receive your
              Google password.
            </li>
            <li>
              <strong>Sign in with Apple.</strong> If you sign in with Apple, we receive your name
              and email address. If you choose "Hide My Email", we receive an Apple private relay
              address instead of your real email.
            </li>
            <li>
              <strong>Email sign-up.</strong> If you sign up with your email address, we receive
              that email address and the name you enter. If you sign in with an emailed link, we use
              your email address to send it.
            </li>
            <li>
              <strong>Step and activity data.</strong> With your permission, the app reads your step
              counts from Apple Health (HealthKit) on iOS, or Health Connect on Android, to track your
              daily goal and streak. We store your daily step total on our servers to settle each
              day's goal. We only read the data needed for this, and we do not write to your health
              records.
            </li>
            <li>
              <strong>Health profile (optional).</strong> When you sign up, or later in My Plan →
              Health focus, you can tell us what you would like walking to help you with (such as
              general health, blood sugar, heart health, blood pressure, weight management,
              mobility, or stress and mood), how active you usually are, and the time of day you
              prefer to walk. Each question is optional. You can skip it, or change your answers at
              any time in My Plan. Your answers are stored on your profile and only you can see
              them.
            </li>
            <li>
              <strong>Goals and app activity.</strong> The step goals you set, your streaks, any
              virtual credits you put on your goals, challenges you join, and your results. Also
              your answers to occasional in-app questions (such as how your first week went), any
              problem you report about a day's result, and any challenge you report.
            </li>
            <li>
              <strong>Push notification tokens.</strong> If you allow notifications, we store a
              push notification token for your device, issued by Apple (iOS) or Google (Android), so
              we can send you reminders and updates about your goals.
            </li>
            <li>
              <strong>Support messages.</strong> When you contact us from the app (Settings → Help),
              we receive your message together with your account ID and email address, so we can
              reply.
            </li>
            <li>
              <strong>Referral codes.</strong> You have a personal referral code and link. If someone
              joins Walk It Off with your link, we record that you referred them. You can see how
              many people joined with your link, but not who they are.
            </li>
            <li>
              <strong>Crash and error reports.</strong> If the app crashes or hits an error, it sends
              a report to our crash-reporting service provider. A report contains technical details
              such as the error, the app version, your device model and operating system version.
              Before a report is sent, the app removes your account ID and scrubs names, email
              addresses and similar details from it. Crash reports are not linked to you and are not
              used to track you.
            </li>
            <li>
              <strong>Usage analytics.</strong> The app records how its features are used, such as
              opening the app, finishing onboarding or joining a challenge, and sends these events to
              our analytics service provider. Events carry a random ID created for that install of
              the app, the app version and basic device details (such as the operating system). They
              never include your name, email address, account ID, step counts or health profile
              answers, and they are not linked to your account or used to track you.
            </li>
            <li>
              <strong>Location (optional).</strong> If you allow location access, the app sends your
              approximate location to our server to get the local weather forecast, so it can suggest
              a good time to walk. Your location is used only for that request and is not stored. The
              app works without location access.
            </li>
            <li>
              <strong>Calendar (optional).</strong> If you allow calendar access, the app checks when
              you're busy today to suggest a free time to walk. Your calendar is read on your phone
              only and is never sent to us.
            </li>
            <li>
              <strong>Credits and payments.</strong> Walk It Off uses virtual credits only. Credits
              have no cash value, no payments are processed, and we do not collect payment or card
              details. If we later offer features involving real money, we will update this policy
              before they launch.
            </li>
            <li>
              <strong>Waitlist details.</strong> When you join the waitlist, we collect the email
              address you provide, and your name or a message if you choose to include them.
            </li>
            <li>
              <strong>Basic technical data.</strong> Our hosting providers automatically receive
              standard information such as your IP address, device or browser type, and app or page
              requests, which we use to keep the service secure and working.
            </li>
          </ul>

          <h2>How we use your information</h2>
          <ul>
            <li>
              To create and sign you in to your account, and to show your name and profile picture
              in the app.
            </li>
            <li>To track your daily step goal, streaks, credits, and challenge results.</li>
            <li>
              To tailor in-app tips and walking suggestions, if you answer the optional health
              profile questions. This is the only thing we use those answers for.
            </li>
            <li>
              To send push notifications, such as goal reminders and daily results, if you allow
              them.
            </li>
            <li>To contact you about your account, and to respond to messages you send us.</li>
            <li>
              To send launch updates, early access details, and walking tips if you joined the
              waitlist. You can unsubscribe at any time.
            </li>
            <li>To keep Walk It Off secure, prevent fraud, and fix problems.</li>
            <li>To find and fix crashes and errors, using crash reports.</li>
            <li>
              To understand which features are used and improve the app, using anonymous usage
              analytics.
            </li>
            <li>To suggest a good time to walk, using the weather and your calendar, if you allow it.</li>
            <li>To see how people found Walk It Off, using referral codes.</li>
          </ul>
          <p>
            Walk It Off gives general wellness information. It is not medical advice and not a
            diagnosis.
          </p>
          <p>
            We do not use your information for third-party advertising, and we do not track you
            across other companies' apps or websites.
          </p>

          <h2>Google user data</h2>
          <p>
            We use the name, email address, and profile picture we receive from Google only to create
            and identify your Walk It Off account and display your profile in the app. We do not sell
            this data, use it for advertising, or transfer it to others except as needed to provide
            the service, to comply with the law, or with your consent. Walk It Off's use and transfer
            of information received from Google APIs adheres to the{' '}
            <a
              href="https://developers.google.com/terms/api-services-user-data-policy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Google API Services User Data Policy
            </a>
            , including the Limited Use requirements.
          </p>

          <h2>Health data</h2>
          <p>
            Step and activity data from Apple Health or Health Connect is used only to
            provide Walk It Off's goal and streak features, and your optional health profile answers
            are used only to tailor in-app tips and walking suggestions. We never sell this health
            information, never use it for advertising or marketing, never share it with data
            brokers, and never send it to analytics services. The only exception is your step total
            during a challenge, which other members of that challenge can see on its leaderboard
            (see below).
            We do not store HealthKit data in iCloud. You can revoke access at any time in the
            Health app (iOS) or Health Connect / Google settings (Android).
          </p>

          <h2>How we share your information</h2>
          <p>
            We do not sell or rent your personal information. Except as described below, we share
            it only with service providers that help us run Walk It Off, such as cloud hosting,
            database and sign-in, push notification, email, crash reporting, product analytics, weather,
            and team messaging
            providers, and only as needed to provide those services. These providers are required to protect your information in line with this policy. We
            may also disclose information if required by law or to protect the rights and safety of
            our users.
          </p>
          <p>
            <strong>Team notifications.</strong> To welcome new members and answer support quickly,
            our team is notified of new sign-ups, support messages, and challenge reports. A
            notification may include the person's name (if given), email address, sign-in method,
            and account ID, and for a support message or report, its content and the challenge
            concerned. These notifications never contain step data or health profile answers.
          </p>
          <p>
            <strong>Challenges.</strong> When you join or create a challenge, the other members of
            that challenge can see your display name (your first name, unless you choose a different
            display name in the app), the number of days you completed your plan and your total steps
            during the challenge, and your steps today. They can also send you a friend request. Your full name is not shown. Nothing else about you is
            shared. Challenges are
            either invite-only (joined with a code or link) or public. A public challenge's name and
            the broad area its creator chose (such as a city or neighborhood, never an address) can
            be seen by anyone using Walk It Off, and anyone can join it, so its members may be people
            you don't know. You can leave a challenge at any time, which removes you from its
            leaderboard. You can report a challenge from its screen, and we may remove challenges
            that break our rules.
          </p>
          <p>
            <strong>Friends.</strong> You can add friends using their personal friend code, and a
            friendship starts only when the other person accepts. People can't search for you by
            name. Your friends can see your display name, your steps today and when they last
            synced, whether you reached today's walking target, and walking stats such as your best
            day, streaks, average daily steps, days on target and steps this month. They can also
            send you a cheer, which may arrive as a notification. Your health focus, credits and
            full name are not shared with friends. You can remove a friend or decline a request at
            any time, and either of you can end the friendship.
          </p>

          <p>
            <strong>Supporter links.</strong> You can create a private link for someone who supports
            you, such as a family member or coach. Anyone with the link can see your display name,
            your steps today, your daily target, your streak and this week's progress, and can send
            you a cheer. You can turn a link off at any time in Settings → Supporters.
          </p>

          <h2>Data retention and deletion</h2>
          <p>
            We keep your account information for as long as your account is active. You can delete
            your account at any time from within the app, or by emailing us. When you delete your
            account, we delete your personal information, including data received from Google or
            Apple, your activity data, your health profile answers, and your push notification
            tokens, support messages and referral records, within 30 days, except
            where we must keep limited records to meet legal obligations. Waitlist information is
            kept until you unsubscribe or ask us to delete it. Crash reports are kept by our crash-reporting
            provider for a limited time (at most 90 days) and then deleted automatically.
          </p>
          <h3 id="delete-account">Delete your Walk It Off account</h3>
          <p>
            To delete your Walk It Off account (from Abimbola Labs LLC) and its data:
          </p>
          <ul>
            <li>
              <strong>In the app:</strong> open Walk It Off, go to Settings, tap{' '}
              <strong>Delete account</strong> and confirm.
            </li>
            <li>
              <strong>Without the app:</strong> email{' '}
              <a href={`mailto:${CONTACT_EMAIL}?subject=Delete%20my%20account`}>{CONTACT_EMAIL}</a>{' '}
              from the email address on your account, with the subject "Delete my account".
            </li>
          </ul>
          <p>
            We delete your account, profile, step and activity data, health profile answers,
            challenge and friend records, supporter links and push notification tokens within 30
            days. Crash reports and anonymous usage analytics are not linked to your account; crash
            reports are deleted automatically within 90 days.
          </p>
          <p>
            You can also remove Walk It Off's access to your Google account at any time at{' '}
            <a
              href="https://myaccount.google.com/permissions"
              target="_blank"
              rel="noopener noreferrer"
            >
              myaccount.google.com/permissions
            </a>
            , or stop using Sign in with Apple in your Apple ID settings.
          </p>

          <h2>Your choices and rights</h2>
          <ul>
            <li>Withdraw permission for health data or notifications in your device settings.</li>
            <li>Skip the health profile questions, or change your answers at any time in My Plan.</li>
            <li>Unsubscribe from our emails using the link in any message.</li>
            <li>
              Ask to see, correct, export, or delete the information we hold about you by emailing{' '}
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
            </li>
            <li>
              Depending on where you live (for example, California or the EU/UK), you may have
              additional rights under local law. We will honor those requests.
            </li>
          </ul>

          <h2>Security</h2>
          <p>
            We use reasonable safeguards, including encryption in transit, to protect your
            information. No method of transmission or storage is completely secure, but we work to
            keep your data safe.
          </p>

          <h2>Children</h2>
          <p>
            Walk It Off is not intended for anyone under 18, and we do not knowingly collect
            information from children. If you believe a child has given us information, contact us
            and we will delete it.
          </p>

          <h2>Changes to this policy</h2>
          <p>
            We may update this policy from time to time. When we do, we will change the "Last
            updated" date above and, for significant changes, notify you in the app or by email.
          </p>

          <h2>Contact us</h2>
          <p>
            Questions about this policy or your data? Email us at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. See also our{' '}
            <a href="/terms">Terms of Service</a>.
          </p>

          <a href="/" className="privacy-back">← Back to Walk It Off</a>
        </div>
      </main>

      <footer className="footer">
        <div className="footer-bottom">
          <span>walkitoff.xyz</span>
          <span>© {currentYear} Walk It Off. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}

export default Privacy;
